/**
 * Build the `internal_reference` string shown in invoicing screens from a delivery payload.
 *
 * Uses contract numbers from the linked recaps (purchase_recaps_data / sales_recaps_data)
 * and falls back to the legacy single `buyer_contract_number` / `seller_contract_number`
 * fields on the delivery for older records.
 *
 * @param {Object} delivery - Delivery object (expects delivery_unique_number and optionally
 *                            purchase_recaps_data / sales_recaps_data arrays).
 * @param {'sale'|'purchase'} side - 'sale' for outgoing docs (proforma, outgoing invoice,
 *                                    credit note, debit note); 'purchase' for incoming
 *                                    docs (PO, incoming invoice).
 * @returns {string} Newline-separated reference string.
 */
export function buildDeliveryInternalReference(delivery, side = 'sale') {
  if (!delivery) return '';

  const lines = [];

  if (delivery.delivery_unique_number) {
    lines.push(`Delivery: ${delivery.delivery_unique_number}`);
  }

  const purchaseRecaps = Array.isArray(delivery.purchase_recaps_data) ? delivery.purchase_recaps_data : [];
  const salesRecaps = Array.isArray(delivery.sales_recaps_data) ? delivery.sales_recaps_data : [];

  const buyerContracts = purchaseRecaps
    .map(r => (r && r.purchase_contract_number ? String(r.purchase_contract_number).trim() : ''))
    .filter(Boolean);

  const sellerContracts = salesRecaps
    .map(r => (r && r.sale_contract_number ? String(r.sale_contract_number).trim() : ''))
    .filter(Boolean);

  // Fall back to legacy single fields if nothing found in recaps
  if (buyerContracts.length === 0 && delivery.buyer_contract_number) {
    buyerContracts.push(String(delivery.buyer_contract_number).trim());
  }
  if (sellerContracts.length === 0 && delivery.seller_contract_number) {
    sellerContracts.push(String(delivery.seller_contract_number).trim());
  }

  if (side === 'purchase') {
    // Incoming invoice / PO - reference the buyer-side contracts (contracts under which we bought)
    if (buyerContracts.length > 0) {
      lines.push(`Buyer Contract: ${buyerContracts.join(', ')}`);
    }
    const purchaseRecapNumbers = purchaseRecaps
      .map(r => (r && r.transaction_number ? r.transaction_number : ''))
      .filter(Boolean);
    if (purchaseRecapNumbers.length > 0) {
      lines.push(`Purchase Recaps: ${purchaseRecapNumbers.join(', ')}`);
    }
  } else {
    // Sales-side docs - show BOTH seller and buyer contracts for audit trail
    if (sellerContracts.length > 0) {
      lines.push(`Seller Contract: ${sellerContracts.join(', ')}`);
    }
    if (buyerContracts.length > 0) {
      lines.push(`Buyer Contract: ${buyerContracts.join(', ')}`);
    }
    const salesRecapNumbers = salesRecaps
      .map(r => (r && r.transaction_number ? r.transaction_number : ''))
      .filter(Boolean);
    if (salesRecapNumbers.length > 0) {
      lines.push(`Sales Recaps: ${salesRecapNumbers.join(', ')}`);
    }
  }

  return lines.join('\n');
}
